import { useCallback, useEffect, useState } from "react";
import { usePrefs } from "./prefs";

declare const __BUILD_ID__: string;
const CHECK_EVERY_MS = 10 * 60_000;

/** Throw away the service worker and its caches, then reload straight from the network. */
export async function forceReload(): Promise<void> {
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.();
    await Promise.all((regs ?? []).map((r) => r.unregister()));
    const keys = await caches?.keys?.();
    await Promise.all((keys ?? []).map((k) => caches.delete(k)));
  } catch {
    /* reload anyway */
  }
  location.reload();
}

async function latestId(): Promise<string | null> {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return null;
    return ((await r.json()) as { id?: string }).id ?? null;
  } catch {
    return null;
  }
}

/** Tells the app when a newer build is deployed (checks on start, on return to the app, and every 10 minutes). */
export function useUpdateAvailable(): { available: boolean; check: () => Promise<boolean> } {
  const [available, setAvailable] = useState(false);
  const autoUpdate = usePrefs((s) => s.autoUpdate);
  const check = useCallback(async () => {
    const id = await latestId();
    const newer = !!id && id !== __BUILD_ID__;
    setAvailable(newer);
    return newer;
  }, []);
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    void check();
    const timer = setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      // Coming back to the app: with auto-update on, switch to the new version right away.
      if ((await check()) && autoUpdate) void forceReload();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check, autoUpdate]);
  return { available, check };
}
